import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  setActiveGroupSlug,
  getCourtStartTime,
  getCrewSchedule,
  getPublishedPageConfig,
} from "../firebaseService";

const courts = ["Center Court", "Court 1", "Court 2", "Court 3", "Court 4"];
const CREW_LABELS = ["A", "B", "C", "D"];
const CREW_COLORS = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2"];

const timeToMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

const PublicCrewTimesPage = () => {
  const { groupSlug } = useParams();
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [courtTimes, setCourtTimes] = useState({});
  const [flexSchedule, setFlexSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load days from published page config
  useEffect(() => {
    if (groupSlug) setActiveGroupSlug(groupSlug);
    getPublishedPageConfig().then((cfg) => {
      const days = cfg.daysOfWeek || [];
      setDaysOfWeek(days);
      if (days.length > 0) {
        const today = new Date();
        const formatted = `${today.getDate()}. ${today.toLocaleString("de-DE", { month: "long" }).replace(/^\w/, (c) => c.toUpperCase())}`;
        setSelectedDay(days.includes(formatted) ? formatted : days[0]);
      }
    });
  }, [groupSlug]);

  const fetchData = useCallback(async () => {
    if (!selectedDay) return;
    setLoading(true);
    try {
      const schedule = await getCrewSchedule(selectedDay);
      if (schedule && schedule.slots && schedule.mode !== "simple") {
        setFlexSchedule(schedule);
        setCourtTimes({});
      } else {
        setFlexSchedule(null);
        const data = {};
        await Promise.all(
          courts.map(async (court) => {
            const courtData = await getCourtStartTime(selectedDay, court);
            if (!courtData.startTime) return;
            const [startHour, startMinute] = courtData.startTime.split(":").map(Number);
            const interval = courtData.isTurnusEnabled ? 45 : 60;
            const timeslots = [];
            const current = new Date();
            current.setHours(startHour, startMinute, 0, 0);
            const end = new Date();
            end.setHours(20, 0, 0, 0);
            while (current <= end) {
              timeslots.push(`${String(current.getHours()).padStart(2, "0")}:${String(current.getMinutes()).padStart(2, "0")}`);
              current.setMinutes(current.getMinutes() + interval);
            }
            const crewCount = courtData.hasThreeCrews ? 3 : 2;
            data[court] = { timeslots };
            for (let i = 0; i < crewCount; i++) {
              data[court][`Crew ${CREW_LABELS[i]}`] = timeslots.map((_, idx) => (idx % crewCount === i ? court : "Pause"));
            }
          })
        );
        setCourtTimes(data);
      }
    } catch (err) {
      console.error("Fehler beim Laden der Einsatzzeiten:", err);
    }
    setLoading(false);
  }, [selectedDay]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderFlexSchedule = () => {
    const { slots, crewCount = 2 } = flexSchedule;
    const allSlots = [];
    Object.entries(slots).forEach(([crew, crewSlots]) => {
      (crewSlots || []).forEach((slot) => allSlots.push({ crew, ...slot }));
    });

    if (allSlots.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
          Keine Einsätze für {selectedDay} konfiguriert.
        </Typography>
      );
    }

    const times = new Set();
    allSlots.forEach((s) => { times.add(s.from); times.add(s.to); });
    const sortedTimes = Array.from(times).sort();
    const [eh, em] = sortedTimes[0].split(":").map(Number);
    const [lh, lm] = sortedTimes[sortedTimes.length - 1].split(":").map(Number);
    const timeline = [];
    let cur = eh * 60 + em;
    const end = lh * 60 + lm;
    while (cur < end) {
      timeline.push(`${String(Math.floor(cur / 60)).padStart(2, "0")}:${String(cur % 60).padStart(2, "0")}`);
      cur += 30;
    }

    const getSlotAt = (crew, timeStr) => {
      const t = timeToMinutes(timeStr);
      return allSlots.find((s) => s.crew === crew && timeToMinutes(s.from) <= t && timeToMinutes(s.to) > t);
    };

    return (
      <TableContainer component={Paper} sx={{ mt: 2, bgcolor: "rgba(255,255,255,0.05)" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, position: "sticky", left: 0, bgcolor: "#1a2138", zIndex: 2, color: "white" }}>Crew</TableCell>
              {timeline.map((t) => (
                <TableCell key={t} align="center" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "rgba(255,255,255,0.7)" }}>{t}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {CREW_LABELS.slice(0, crewCount).map((crew, crewIdx) => (
              <TableRow key={crew}>
                <TableCell sx={{ fontWeight: 700, color: CREW_COLORS[crewIdx], position: "sticky", left: 0, bgcolor: "#1a2138", zIndex: 1 }}>
                  Crew {crew}
                </TableCell>
                {timeline.map((t) => {
                  const slot = getSlotAt(crew, t);
                  return (
                    <TableCell
                      key={t}
                      align="center"
                      sx={{
                        fontSize: "0.7rem",
                        bgcolor: slot ? `${CREW_COLORS[crewIdx]}99` : "transparent",
                        color: slot ? "white" : "rgba(255,255,255,0.3)",
                        py: 0.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {slot ? slot.court : "—"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderSimpleSchedule = () => {
    const courtsWithData = Object.keys(courtTimes);
    if (courtsWithData.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
          Keine Startzeiten für {selectedDay} konfiguriert.
        </Typography>
      );
    }
    return courtsWithData.map((court) => (
      <Box key={court} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ color: "white", mb: 1, textAlign: "center" }}>{court}</Typography>
        <TableContainer component={Paper} sx={{ bgcolor: "rgba(255,255,255,0.05)" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Crew</TableCell>
                {courtTimes[court].timeslots.map((t) => (
                  <TableCell key={t} align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>{t}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(courtTimes[court])
                .filter((k) => k !== "timeslots")
                .map((crew, crewIdx) => (
                  <TableRow key={crew}>
                    <TableCell sx={{ color: CREW_COLORS[crewIdx], fontWeight: 700 }}>{crew}</TableCell>
                    {courtTimes[court][crew].map((slot, i) => (
                      <TableCell
                        key={i}
                        align="center"
                        sx={{
                          fontSize: "0.7rem",
                          bgcolor: slot !== "Pause" ? `${CREW_COLORS[crewIdx]}99` : "transparent",
                          color: slot !== "Pause" ? "white" : "rgba(255,255,255,0.3)",
                          py: 0.5,
                        }}
                      >
                        {slot !== "Pause" ? slot : "—"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    ));
  };

  return (
    <Box sx={{ bgcolor: "#191f34", minHeight: "100vh", px: 2, py: 3 }}>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
          <AccessTimeIcon sx={{ color: "#90caf9" }} />
          <Typography variant="h5" sx={{ color: "white", fontWeight: 700 }}>
            Einsatzzeiten der Crews
          </Typography>
        </Box>

        {selectedDay && (
          <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.7)", textAlign: "center", mb: 3 }}>
            {selectedDay}
          </Typography>
        )}

        {loading ? (
          <CircularProgress sx={{ display: "block", mx: "auto", mt: 6, color: "white" }} />
        ) : (
          <>
            {flexSchedule ? renderFlexSchedule() : renderSimpleSchedule()}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PublicCrewTimesPage;
